import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CalendarClock, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * StickyBookDemo — floating "Book a Demo" pill that appears after the visitor
 * scrolls past the hero. Session-dismissible; auto-suppressed once the
 * visitor has already opened /request-demo this session (so it stops nagging
 * warm leads); fires shown/click/dismiss events into pricing-funnel analytics.
 *
 * Placement: bottom-right on desktop, bottom-center full-width on mobile.
 */
const DISMISS_KEY = "tr_sticky_demo_dismissed";
const VISITED_DEMO_KEY = "tr_demo_visited";
const SHOW_AFTER_PX = 600;

function isSuppressed() {
  try {
    return !!(
      sessionStorage.getItem(DISMISS_KEY) ||
      sessionStorage.getItem(VISITED_DEMO_KEY)
    );
  } catch {
    return false;
  }
}

export function StickyBookDemo({ placement = "landing" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const [shownFired, setShownFired] = useState(false);

  // Suppress the pill for the rest of the session once the visitor lands on
  // /request-demo — from any entry point (nav, hero, pill, footer, pricing).
  useEffect(() => {
    if (location.pathname === "/request-demo") {
      try {
        sessionStorage.setItem(VISITED_DEMO_KEY, "1");
      } catch {
        /* no-op */
      }
      setSuppressed(true);
    } else if (isSuppressed()) {
      setSuppressed(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (suppressed) return undefined;
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setVisible(y > SHOW_AFTER_PX);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [suppressed]);

  useEffect(() => {
    if (visible && !suppressed && !shownFired) {
      trackEvent("sticky_demo_shown", { placement });
      setShownFired(true);
    }
  }, [visible, suppressed, shownFired, placement]);

  if (suppressed || !visible) return null;

  const book = () => {
    trackEvent("sticky_demo_click", { placement });
    try {
      sessionStorage.setItem(VISITED_DEMO_KEY, "1");
    } catch {
      /* no-op */
    }
    navigate("/request-demo");
  };
  const dismiss = () => {
    trackEvent("sticky_demo_dismiss", { placement });
    setSuppressed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* no-op */
    }
  };

  return (
    <div
      className="fixed z-40 bottom-4 right-4 sm:right-6 max-sm:left-4 max-sm:right-4"
      data-testid="sticky-book-demo"
    >
      <div className="flex items-center gap-1 rounded-full border border-cyan-400/40 bg-app/95 backdrop-blur shadow-2xl shadow-cyan-500/10 p-1 pl-4">
        <CalendarClock className="w-4 h-4 text-cyan-300 shrink-0" />
        <span className="text-xs text-slate-200 pr-2 hidden sm:inline">
          See TotalReach in a live 20-min demo
        </span>
        <span className="text-xs text-slate-200 pr-2 sm:hidden">See a live demo</span>
        <button
          onClick={book}
          data-testid="sticky-book-demo-btn"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-xs px-4 py-2 hover:opacity-90"
        >
          Book a Demo
        </button>
        <button
          onClick={dismiss}
          data-testid="sticky-book-demo-dismiss"
          aria-label="Dismiss"
          className="text-slate-500 hover:text-white p-1.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
